import { Draft } from "immer";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import { id } from "../utils/string.ts";

import { Branch, Group, Logic, OPERATOR } from "../types/logic.ts";
import { Rule } from "../types/rule.ts";
import { Action, ActionType, Field } from "../types/zustand.ts";

type Set = {
    (
        partial:
            | LogicState
            | Partial<LogicState>
            | ((state: Draft<LogicState>) => void),
        replace?: false,
    ): void;
    (
        state: LogicState | ((state: Draft<LogicState>) => void),
        replace: true,
    ): void;
};

type Get = () => LogicState;

type LogicState = {
    activeFieldId: string | null;
    fields: Field[];
    logics: Logic[];

    setActiveFieldId: (activeFieldId: string) => void;
    setFields: (fields: Field[]) => void;
    setLogics: (logics: Logic[]) => void;
    dispatch: (action: Action) => void;
};

const reducer = (state: LogicState, action: Action): void => {
    switch (action.type) {
        case ActionType.ADD_BRANCH:
            // Find the logic and add a new branch
            {
                const { fieldId } = action.payload;
                const logic = state.logics.find((l) => l.fieldId === fieldId);
                if (!logic) return;

                const branches = logic.branches.length;
                logic.branches.push({
                    id: id(),
                    name: `Branch ${branches + 1}`,
                    targetId: logic.defaultTargetId, // This would need to be provided in a real implementation
                    groups: [
                        {
                            id: id(),
                            name: `Group ${1}`,
                            rules: [
                                {
                                    id: id(),
                                    sourceId: fieldId,
                                    operator: OPERATOR.EQUAL,
                                    expectedValue: "",
                                },
                            ],
                        },
                    ],
                });
            }
            break;

        case ActionType.ADD_GROUP:
            // Find the branch and add a new group to it
            {
                const { branchId } = action.payload;
                for (const logic of state.logics) {
                    const branch = logic.branches.find(
                        (b) => b.id === branchId,
                    );
                    if (branch) {
                        const groups = branch.groups.length;
                        branch.groups.push({
                            id: id(),
                            name: `Group ${groups + 1}`,
                            rules: [
                                {
                                    id: id(),
                                    sourceId: logic.fieldId,
                                    operator: OPERATOR.EQUAL,
                                    expectedValue: "",
                                },
                            ],
                        });
                        break;
                    }
                }
            }
            break;

        case ActionType.ADD_RULE:
            // Find the group and add a new rule to it
            {
                const { groupId, sourceId, operator, expectedValue } =
                    action.payload;
                for (const logic of state.logics) {
                    for (const branch of logic.branches) {
                        const group = branch.groups.find(
                            (g) => g.id === groupId,
                        );
                        if (group) {
                            const rule: Rule = {
                                id: id(),
                                sourceId,
                                operator,
                                expectedValue,
                            };
                            group.rules.push(rule);
                            break;
                        }
                    }
                }
            }
            break;

        case ActionType.EDIT_LOGIC:
            // Find the logic and update its defaultTargetId
            {
                const { fieldId, defaultTargetId } = action.payload;
                const logic = state.logics.find((l) => l.fieldId === fieldId);
                if (!logic) return;
                logic.defaultTargetId = defaultTargetId;
            }
            break;

        case ActionType.EDIT_BRANCH:
            // Find the branch and update its targetId
            {
                const { branchId, targetId } = action.payload;
                for (const logic of state.logics) {
                    const branchIndex = logic.branches.findIndex(
                        (b) => b.id === branchId,
                    );
                    if (branchIndex === -1) continue;
                    logic.branches[branchIndex].targetId = targetId;
                }
            }
            break;

        case ActionType.EDIT_RULE:
            // Find the rule and update its properties
            {
                const { ruleId, sourceId, operator, expectedValue } =
                    action.payload;
                for (const logic of state.logics) {
                    for (const branch of logic.branches) {
                        for (const group of branch.groups) {
                            // Find rule by sourceId (assuming it's unique within a group)
                            const ruleIndex = group.rules.findIndex(
                                (r) => r.id === ruleId,
                            );
                            if (ruleIndex === -1) continue;
                            const currRule = group.rules[ruleIndex];
                            group.rules[ruleIndex] = {
                                id: ruleId,
                                sourceId: sourceId ?? currRule.sourceId,
                                operator: operator ?? currRule.operator,
                                expectedValue:
                                    expectedValue ?? currRule.expectedValue,
                            };
                            break;
                        }
                    }
                }
            }
            break;

        case ActionType.REMOVE_BRANCH:
            // Remove the branch with the given ID
            {
                const { branchId } = action.payload;
                for (const logic of state.logics) {
                    const branchIndex = logic.branches.findIndex(
                        (b) => b.id === branchId,
                    );
                    if (branchIndex === -1) continue;
                    logic.branches.splice(branchIndex, 1);
                }
            }
            break;

        case ActionType.REMOVE_GROUP:
            // Remove the group with the given ID from its branch
            {
                const { groupId } = action.payload;
                for (const logic of state.logics) {
                    for (const branch of logic.branches) {
                        const groupIndex = branch.groups.findIndex(
                            (g) => g.id === groupId,
                        );
                        if (groupIndex === -1) continue;
                        branch.groups.splice(groupIndex, 1);
                    }
                }
            }
            break;

        case ActionType.REMOVE_RULE:
            // Remove the rule with the given ID from its group
            {
                const { ruleId } = action.payload;
                for (const logic of state.logics) {
                    for (const branch of logic.branches) {
                        for (const group of branch.groups) {
                            const ruleIndex = group.rules.findIndex(
                                (r) => r.id === ruleId,
                            );
                            if (ruleIndex === -1) continue;
                            group.rules.splice(ruleIndex, 1);
                        }
                    }
                }
            }
            break;

        case ActionType.DUPLICATE_BRANCH:
            // Find the group, clone it with new IDs, and add it to the same branch
            {
                const { branchId } = action.payload;
                for (const logic of state.logics) {
                    const branchIndex = logic.branches.findIndex(
                        (b) => b.id === branchId,
                    );
                    if (branchIndex === -1) continue;
                    const originalBranch = logic.branches[branchIndex];
                    if (!originalBranch) continue;

                    const branches = logic.branches.length;

                    // Deep clone the branch and its groups
                    const newBranch: Branch = {
                        id: id(),
                        name: `${originalBranch.name}/${branches + 1}`,
                        targetId: originalBranch.targetId,
                        groups: originalBranch.groups.map((group) => ({
                            ...group,
                        })),
                    };

                    logic.branches.splice(branchIndex + 1, 0, newBranch);
                }
            }
            break;

        case ActionType.DUPLICATE_GROUP:
            // Find the group, clone it with new IDs, and add it to the same branch
            {
                const { groupId } = action.payload;
                for (const logic of state.logics) {
                    for (const branch of logic.branches) {
                        const groupIndex = branch.groups.findIndex(
                            (g) => g.id === groupId,
                        );
                        if (groupIndex === -1) continue;
                        const originalGroup = branch.groups[groupIndex];
                        if (!originalGroup) continue;

                        const groups = branch.groups.length;

                        // Deep clone the group and its rules
                        const newGroup: Group = {
                            id: id(),
                            name: `${originalGroup.name} ${groups + 1}`,
                            rules: originalGroup.rules.map((rule) => ({
                                ...rule,
                            })),
                        };

                        branch.groups.splice(groupIndex + 1, 0, newGroup);
                    }
                }
            }
            break;

        case ActionType.SET_ACTIVE_FIELD_ID:
            {
                const { fieldId } = action.payload;
                state.activeFieldId = fieldId;
            }
            break;

        case ActionType.ADD_FIELD:
            {
                // First, add the field to the fields array
                // Then, create a new logic for the field
                // Then, add the logic to the logics array

                const field = action.payload;
                const newFieldIndex = field.index;

                console.log("New Field Index", newFieldIndex);

                // The followings are the previous and next fields as if the new field is not added into the fields array
                const prevField = state.fields.find(
                    (f) => f.index === newFieldIndex - 1,
                );
                const prevFieldLogic = state.logics.find(
                    (l) => l.fieldId === prevField?.id,
                );
                // Point the default target id of the previous field logic to the new field id
                if (prevFieldLogic) {
                    prevFieldLogic.defaultTargetId = field.id;
                }

                const nextField = state.fields.find(
                    (f) => f.index === newFieldIndex,
                );

                console.log("Prev Field", prevField?.id, prevField?.title);
                console.log("Next field", nextField?.id, nextField?.title);

                // Add the field to the fields array considering the index of the field
                // Ex:- If the index is 3, then the field should be added at index 3
                // Also, change the rest of the already added fields' indexes
                state.fields.forEach((f, index) => {
                    if (index >= newFieldIndex) {
                        f.index = index + 1;
                    }
                });
                state.fields.splice(newFieldIndex, 0, field);

                // Remove the duplicate fields by their fieldId
                state.fields = state.fields.filter(
                    (f, index, self) =>
                        index === self.findIndex((t) => t.id === f.id),
                );

                if (field.type !== "url_redirect") {
                    // Point the default target id of the new field logic to the next field id
                    const logic: Logic = {
                        fieldId: field.id,
                        branches: [],
                        defaultTargetId: nextField?.id ?? "",
                    };
                    state.logics.push(logic);
                }
            }
            break;

        case ActionType.CHANGE_FIELD_TITLE:
            {
                const { fieldId, title } = action.payload;
                const field = state.fields.find((f) => f.id === fieldId);
                if (field) field.title = title;
            }
            break;

        case ActionType.CHANGE_FIELD_ORDER:
            {
                console.log("CHANGE_FIELD_ORDER", action.payload);
                const { newFields } = action.payload;

                // Change index and the position of the fields
                state.fields = newFields.map((f, index) => ({
                    ...f,
                    index: index + 1,
                }));

                // Reorder the position of each logic inside logics array based on the new field index
                // EX:- If the newFields array is [field1, field2, field3], then the logics array should be [logic1, logic2, logic3]
                // If the newFields array is [field1, field3, field2], then the logics array should be [logic1, logic3, logic2]

                // const logics: Logic[] = [];
                // newFields.forEach((f, index) => {
                //     const logic = state.logics.find((l) => l.fieldId === f.id);
                //     if (logic) {
                //         logics.push(logic);
                //     }
                // });
                // state.logics = logics;
            }
            break;

        case ActionType.REMOVE_FIELD:
            {
                console.log("REMOVE_FIELD", action.payload);
                const { fieldId } = action.payload;
                // Remove the field from the fields array
                const fieldToRemove = state.fields.find(
                    (f) => f.id === fieldId,
                );

                console.log(
                    "Field to remove",
                    fieldToRemove?.id,
                    fieldToRemove?.title,
                );

                if (!fieldToRemove) return;

                const prevField = state.fields.find(
                    (f) => f.index === fieldToRemove?.index - 1,
                );
                const prevFieldLogic = state.logics.find(
                    (l) => l.fieldId === prevField?.id,
                );

                const nextField = state.fields.find(
                    (f) => f.index === fieldToRemove?.index + 1,
                );
                const nextFieldLogic = state.logics.find(
                    (l) => l.fieldId === nextField?.id,
                );

                console.log("Prev Field", prevField?.id, prevField?.title);
                console.log("Next field", nextField?.id, nextField?.title);

                // Change all the logics that pointed towards the field to be removed
                // In this case, default target id should be the next field to the found logic
                state.logics.forEach((l) => {
                    // Find the field that the current logic is attached with
                    const thisField = state.fields.find(
                        (f) => f.id === l.fieldId,
                    );
                    if (!thisField) return;

                    const _nextField = state.fields.find(
                        (f) => f.index === thisField?.index + 1,
                    );
                    // if (!_nextField) return;

                    // Check if the default target id of the logic is the field to be removed
                    if (l.defaultTargetId === fieldId) {
                        // Update the default target id of the logic
                        l.defaultTargetId = _nextField?.id ?? "";
                    }

                    // Change all the branches that pointed towards the field to be removed
                    l.branches.forEach((b) => {
                        if (b.targetId === fieldId) {
                            b.targetId = _nextField?.id ?? "";
                        }
                    });
                });

                // Remove the logic
                state.logics = state.logics.filter(
                    (l) => l.fieldId !== fieldId,
                );

                // Reorder the field indexes
                state.fields.forEach((f, index) => {
                    if (f.index > fieldToRemove?.index) {
                        f.index = f.index - 1;
                    }
                });

                // Change the default target id of the previous field to the next field
                if (prevFieldLogic) {
                    prevFieldLogic.defaultTargetId = nextField?.id ?? "";
                }

                // Change the default target id of the next field to the next field
                // if (nextFieldLogic && nextFieldLogic.defaultTargetId === fieldId) {
                //     nextFieldLogic.defaultTargetId = nextField?.id ?? "";
                // }

                // Remove the logics containing the default target id as an empty string
                // state.logics = state.logics.filter(
                //     (l) => l.defaultTargetId !== "",
                // );

                // Remove the field from the fields array
                state.fields = state.fields.filter((f) => f.id !== fieldId);
            }
            break;

        case ActionType.CHANGE_FIELD_INDEX:
            {
                const { fieldId, newIndex } = action.payload;
                const field = state.fields.find((f) => f.id === fieldId);
                if (!field) return;

                // Change index of the field
                field.index = newIndex;

                // Change index of the rest of the fields
                state.fields.forEach((f) => {
                    if (f.index >= newIndex) {
                        f.index = f.index + 1;
                    }
                });

                // Change index of the logics
                state.logics.forEach((l) => {
                    if (l.fieldId === fieldId) {
                        l.fieldId = field.id;
                    }
                });
            }
            break;

        case ActionType.RESET_LOGIC:
            {
                const fields = state.fields;

                // Recreate the logics array with sequential navigation
                const logics: Logic[] = [];
                for (const field of fields) {
                    // Get the next field
                    const nextField = fields.find(
                        (f) => f.index === field.index + 1,
                    );
                    logics.push({
                        fieldId: field.id,
                        branches: [],
                        defaultTargetId: nextField?.id ?? "",
                    });
                }

                state.logics = logics;
            }
            break;
    }
};

export const useLogicStore = create<LogicState>()(
    immer(
        (set: Set, get: Get) =>
            ({
                activeFieldId: null,
                fields: [],
                logics: [],

                dispatch: (action: Action) =>
                    set((state) => {
                        reducer(state, action);
                    }),

                setFields: (fields: Field[]) => set({ fields }),
                setLogics: (logics: Logic[]) => set({ logics }),
                setActiveFieldId: (activeFieldId: string) =>
                    set({ activeFieldId }),
            } as LogicState),
    ),
);
